import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Container, Typography, Grid, Paper, Chip, IconButton, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { 
  Security, 
  LocationOn, 
  Code, 
  ShoppingCart, 
  Psychology, 
  Storage,
  GitHub,
  Launch,
  Close
} from '@mui/icons-material';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const projects = [
    {
      id: 1,
      title: "Distributed & Encrypted File System",
      subtitle: "SEDFS - Secure Distributed File System",
      description: "A defensive distributed and encrypted file system that ensures ACID properties across replicated servers with advanced security features.",
      fullDescription: `Developed a comprehensive distributed and encrypted file system (SEDFS) that defines how files are named, stored, and retrieved from different replicated servers while ensuring ACID properties. The system provides secure user authentication and granular permission controls, allowing users to manage access to files, folders, and encryption keys. Implemented Fernet encryption for file contents and SHA256 hashing for file/directory names, ensuring data integrity and confidentiality. Established continuous communication protocols between master and replica servers to maintain data consistency and prevent unauthorized access.`,
      technologies: ["Python", "Multi-threading", "HashLib", "Sockets", "Fernet"],
      icon: <Security />,
      color: "#4CAF50",
      gradient: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
      features: [
        "ACID properties across replicated servers",
        "Fernet encryption for file contents",
        "SHA256 hashing for file/directory names",
        "User permission management",
        "Master-replica communication protocols"
      ],
      period: "Feb 2022 - Present",
      institution: "University of Maryland Baltimore County"
    },
    {
      id: 2,
      title: "Location Privacy (Food Delivery System)",
      subtitle: "K-Anonymity with Obfuscation",
      description: "Novel location privacy technique combining K-Anonymity and Obfuscation for secure food delivery while protecting user location data.",
      fullDescription: `Created an innovative location privacy solution for food delivery systems using a combination of K-Anonymity and Obfuscation techniques. The system enables users to share their location with delivery personnel while maintaining complete privacy control. By implementing K-anonymity with obfuscation, the delivery person never receives the exact user location - instead, a randomized location point is generated based on user preferences and privacy settings. This approach allows customers to choose their privacy level and travel distance preferences, making it an optimal solution for location privacy in delivery applications.`,
      technologies: ["K-Anonymity", "Obfuscation", "Google Maps SDK", "ReactJS", "NodeJS", "Firebase"],
      icon: <LocationOn />,
      color: "#2196F3",
      gradient: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
      features: [
        "K-Anonymity with Obfuscation",
        "User preference-based privacy",
        "Randomized location generation",
        "Google Maps SDK integration",
        "Real-time delivery tracking"
      ],
      period: "Jan 2022 - Present",
      institution: "University of Maryland Baltimore County"
    },
    {
      id: 3,
      title: "MEME's Classification",
      subtitle: "Multi-Modal Emotion Analysis",
      description: "Advanced meme classification system using deep learning to analyze emotions, humor, sarcasm, and motivation levels from visual and textual content.",
      fullDescription: `Developed a sophisticated two-phase meme classification system that progressively analyzes emotions in increasing depth. Phase 1 focuses on broad emotional categorization (positive, negative, neutral) using pre-trained models like ResNet50 and VGG16 with custom layers including Conv2D, Dense, Dropout, and Global Average Pooling. Phase 2 delves into nuanced emotional aspects, classifying memes based on humor, sarcasm, offensiveness, and motivation levels. The system integrates both image and text analysis using Bidirectional LSTM, Conv1D, and Dense layers for comprehensive emotional understanding. This multi-modal approach captures the synergistic effect of visual and textual content for holistic emotion analysis.`,
      technologies: ["Python", "TensorFlow", "ResNet50", "VGG16", "LSTM", "NLP"],
      icon: <Psychology />,
      color: "#9C27B0",
      gradient: "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)",
      features: [
        "Two-phase emotion analysis",
        "Pre-trained CNN models (ResNet50, VGG16)",
        "Bidirectional LSTM for text processing",
        "Multi-modal fusion approach",
        "Fine-grained emotion classification"
      ],
      period: "Jan 2023 - May 2023",
      institution: "University of Maryland Baltimore County"
    },
    {
      id: 4,
      title: "Fail-Silent Replicated Token Manager",
      subtitle: "Atomic Semantics with Quorum Protocol",
      description: "Client-server token manager supporting replication and atomic semantics with fail-silent behavior and quorum protocol implementation.",
      fullDescription: `Implemented a robust client-server token manager that supports replication and atomic semantics using the Read-One-Write-All quorum protocol. The system operates in a client-server model where each shared token has access points for single writer and multiple reader nodes. Key features include fail-silent behavior for server nodes, where a server may "crash-stop" for operations on a particular token by indefinitely postponing responses to all operations on that token after a specified time. This ensures system reliability and consistency even in the presence of node failures.`,
      technologies: ["Go", "Quorum Protocol", "gRPC", "Protocol Buffer"],
      icon: <Storage />,
      color: "#FF9800",
      gradient: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
      features: [
        "Read-One-Write-All quorum protocol",
        "Fail-silent behavior implementation",
        "Atomic semantics support",
        "gRPC communication",
        "Protocol Buffer serialization"
      ],
      period: "Jan 2022 - May 2022",
      institution: "University of Maryland Baltimore County"
    },
    {
      id: 5,
      title: "Personal Portfolio",
      subtitle: "Modern React Portfolio",
      description: "A modern, responsive personal portfolio website built with React, featuring dark/light themes and smooth animations.",
      fullDescription: `Developed a modern, responsive personal portfolio website using React with Material-UI components and Framer Motion animations. The portfolio features a dark/light theme toggle, smooth scrolling navigation, and interactive components. Includes sections for about, experience, education, skills, and contact with modern design principles and mobile-first responsive design. The site showcases professional experience and projects with engaging animations and user-friendly interface.`,
      technologies: ["React", "Material-UI", "Framer Motion", "CSS3", "JavaScript"],
      icon: <Code />,
      color: "#E91E63",
      gradient: "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
      features: [
        "Dark/Light theme toggle",
        "Responsive design",
        "Smooth animations",
        "Interactive components",
        "Modern UI/UX"
      ],
      period: "Nov 2020 - Present",
      institution: "Personal Project"
    },
    {
      id: 6,
      title: "Shop-Store",
      subtitle: "E-commerce Platform",
      description: "Full-stack e-commerce platform with user authentication, product management, and secure payment processing.",
      fullDescription: `Built a comprehensive e-commerce platform using the MERN stack (MongoDB, Express.js, React, Node.js). The application includes user authentication, product catalog management, shopping cart functionality, order processing, and secure payment integration. Features include responsive design, real-time inventory updates, user reviews and ratings, and admin dashboard for store management. The platform ensures secure transactions and provides an intuitive shopping experience for customers.`,
      technologies: ["React", "Node.js", "MongoDB", "Express.js", "JWT"],
      icon: <ShoppingCart />,
      color: "#607D8B",
      gradient: "linear-gradient(135deg, #607D8B 0%, #455A64 100%)",
      features: [
        "User authentication & authorization",
        "Product catalog management",
        "Shopping cart functionality",
        "Secure payment processing",
        "Admin dashboard"
      ],
      period: "Apr 2020 - Present",
      institution: "Personal Project"
    }
  ];

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProject(null);
  };

  return (
    <Box
      component="section"
      id="projects"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'var(--bg-primary)',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'var(--font-display)',
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                mb: 2,
                color: 'var(--text-primary)',
              }}
            >
              Featured Projects
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.125rem',
                color: 'var(--text-secondary)',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              A collection of innovative projects showcasing expertise in distributed systems, 
              security, machine learning, and full-stack development.
            </Typography>
          </Box>
        </motion.div>

        {/* Projects Grid */}
        <Grid container spacing={4} alignItems="stretch">
          {projects.map((project, index) => (
            <Grid item xs={12} md={6} lg={4} key={project.id} style={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ flex: 1, display: 'flex' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    height: '100%',
                    minHeight: 340,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 'var(--border-radius)',
                    border: '1px solid var(--border)',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'var(--transition)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 'var(--shadow-lg)',
                      '& .project-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                    },
                  }}
                  onClick={() => handleProjectClick(project)}
                >
                  {/* Project Header */}
                  <Box
                    sx={{
                      background: project.gradient,
                      p: 3,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      className="project-icon"
                      sx={{
                        color: 'white',
                        fontSize: '2.5rem',
                        mb: 2,
                        transition: 'var(--transition)',
                      }}
                    >
                      {project.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        mb: 1,
                        lineHeight: 1.2,
                      }}
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 500,
                      }}
                    >
                      {project.subtitle}
                    </Typography>
                  </Box>

                  {/* Project Content */}
                  <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'var(--text-secondary)',
                        mb: 3,
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {project.description}
                    </Typography>

                    {/* Technologies */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--text-muted)',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 1,
                          display: 'block',
                        }}
                      >
                        Technologies
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {project.technologies.slice(0, 3).map((tech) => (
                          <Chip
                            key={tech}
                            label={tech}
                            size="small"
                            sx={{
                              backgroundColor: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border)',
                              fontSize: '0.7rem',
                            }}
                          />
                        ))}
                        {project.technologies.length > 3 && (
                          <Chip
                            label={`+${project.technologies.length - 3}`}
                            size="small"
                            sx={{
                              backgroundColor: 'var(--primary)',
                              color: 'white',
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Period & Institution */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--text-muted)',
                          fontWeight: 500,
                        }}
                      >
                        {project.period}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--primary)',
                          fontWeight: 600,
                        }}
                      >
                        {project.institution}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Project Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 'var(--border-radius)',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
          },
        }}
      >
        {selectedProject && (
          <>
            <DialogTitle
              sx={{
                background: selectedProject.gradient,
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ fontSize: '2rem' }}>
                  {selectedProject.icon}
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {selectedProject.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedProject.subtitle}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseDialog}
                sx={{ color: 'white' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
              <Typography
                variant="body1"
                sx={{
                  color: 'var(--text-secondary)',
                  mb: 4,
                  lineHeight: 1.7,
                }}
              >
                {selectedProject.fullDescription}
              </Typography>

              {/* Key Features */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: 'var(--text-primary)',
                  }}
                >
                  Key Features
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedProject.features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: selectedProject.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Technologies */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: 'var(--text-primary)',
                  }}
                >
                  Technologies Used
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedProject.technologies.map((tech) => (
                    <Chip
                      key={tech}
                      label={tech}
                      sx={{
                        backgroundColor: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border)',
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Project Info */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 2,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'var(--text-muted)',
                      mb: 0.5,
                    }}
                  >
                    Period
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {selectedProject.period}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'var(--text-muted)',
                      mb: 0.5,
                    }}
                  >
                    Institution
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: 'var(--primary)',
                    }}
                  >
                    {selectedProject.institution}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Projects; 